import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield,
  MapPin,
  BadgeCheck,
  Clock,
  FileText,
  Banknote,
  Wrench,
  Hammer,
  CloudRain,
  CheckCircle2,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { SEO } from "@/components/SEO";

/* ------------------------------------------------------------------ */
/* Shared visual building blocks for the area-service page templates   */
/* ------------------------------------------------------------------ */

/** A two-column H3 + body block with a leading icon card. */
function FeatureBlock({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-5 items-start">
      <div className="shrink-0 w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
        <Icon className="w-6 h-6 text-accent" />
      </div>
      <div className="flex-1">
        <h3 className="text-xl font-semibold mb-2 text-foreground">{title}</h3>
        <p className="text-muted-foreground leading-relaxed text-[15px]">
          {children}
        </p>
      </div>
    </div>
  );
}

/** A numbered process step card with an accent numeral. */
function ProcessStep({
  step,
  title,
  children,
}: {
  step: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative bg-card border rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-accent text-accent-foreground font-bold text-sm shrink-0">
          {step}
        </span>
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      </div>
      <p className="text-muted-foreground leading-relaxed text-[15px] pl-11">
        {children}
      </p>
    </div>
  );
}

/** A bordered callout box for key facts / TL;DR-style notes. */
function Callout({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border-l-4 border-accent bg-accent/5 p-5 my-2">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5 text-accent" />
        <h4 className="font-bold text-foreground">{title}</h4>
      </div>
      <p className="text-muted-foreground leading-relaxed text-[15px]">
        {children}
      </p>
    </div>
  );
}

/** A simple cost-range stat tile. */
function CostTile({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="bg-card border rounded-xl p-5 text-center shadow-sm">
      <p className="text-2xl md:text-3xl font-bold text-accent">{value}</p>
      <p className="text-sm font-medium text-foreground mt-1">{label}</p>
      <p className="text-xs text-muted-foreground mt-1">{note}</p>
    </div>
  );
}

export interface AreaFaq {
  question: string;
  answer: string;
}

export interface AreaPageData {
  city: string;
  citySlug: string;
  serviceName: string;
  title: string;
  description: string;
  h1: string;
  heroIntro: string;
  ctaLabel?: string;
  faqs: AreaFaq[];
  schema: Record<string, any>[];
  canonical: string;
}

/* ------------------------------------------------------------------ */
/* The shared page shell — used by every area-service template          */
/* ------------------------------------------------------------------ */

export function AreaPageShell({
  data,
  heroImage,
  heroAlt,
  whyBlocks,
  costBlocks,
  processSteps,
  materialBlocks,
  extraCostTiles,
  extraCallout,
}: {
  data: AreaPageData;
  heroImage: string;
  heroAlt: string;
  whyBlocks: { icon: LucideIcon; title: string; text: string }[];
  costBlocks: { title: string; text: string }[];
  processSteps: { title: string; text: string }[];
  materialBlocks: { icon: LucideIcon; title: string; text: string }[];
  extraCostTiles?: { label: string; value: string; note: string }[];
  extraCallout?: { icon: LucideIcon; title: string; text: string };
}) {
  const ctaLabel = data.ctaLabel ?? "Get Your Free Inspection";
  return (
    <>
      <SEO
        title={data.title}
        description={data.description}
        canonical={data.canonical}
        schema={data.schema}
      />
      <div className="flex flex-col min-h-screen">
        {/* Hero */}
        <section className="relative py-24 md:py-32 bg-primary text-primary-foreground overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <img
              src={heroImage}
              alt={heroAlt}
              className="w-full h-full object-cover"
            />
          </div>
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 60%) 60%, transparent 100%)",
            }}
          />
          <div className="container relative z-10 px-4">
            <div className="max-w-3xl space-y-6">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
                {data.h1}
              </h1>
              <p className="text-xl text-primary-foreground/85 leading-relaxed">
                {data.heroIntro}
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  asChild
                >
                  <Link to="/contact">{ctaLabel}</Link>
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="bg-white/10 text-white border-white/20 hover:bg-white/20"
                  asChild
                >
                  <a href="tel:6397397969">Call (639) 739-7969</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* H2 #1 — Why Choose Us */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-10 text-foreground">
              Why {data.city} Homeowners Choose Us for {data.serviceName}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {whyBlocks.map((b, i) => (
                <FeatureBlock key={i} icon={b.icon} title={b.title}>
                  {b.text}
                </FeatureBlock>
              ))}
            </div>
          </div>
        </section>

        {/* H2 #2 — Cost */}
        <section className="py-16 md:py-20 bg-muted/40">
          <div className="container px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-8 text-foreground">
              How Much Does {data.serviceName} Cost in {data.city}?
            </h2>
            {extraCostTiles && extraCostTiles.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {extraCostTiles.map((t, i) => (
                  <CostTile
                    key={i}
                    label={t.label}
                    value={t.value}
                    note={t.note}
                  />
                ))}
              </div>
            )}
            <div className="grid md:grid-cols-3 gap-8">
              {costBlocks.map((b, i) => (
                <FeatureBlock
                  key={i}
                  icon={[Banknote, Clock, BadgeCheck][i % 3]}
                  title={b.title}
                >
                  {b.text}
                </FeatureBlock>
              ))}
            </div>
            {extraCallout && (
              <div className="mt-8">
                <Callout icon={extraCallout.icon} title={extraCallout.title}>
                  {extraCallout.text}
                </Callout>
              </div>
            )}
          </div>
        </section>

        {/* H2 #3 — Process */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-10 text-foreground">
              Our {data.serviceName} Process in {data.city}
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {processSteps.map((s, i) => (
                <ProcessStep key={i} step={i + 1} title={s.title}>
                  {s.text}
                </ProcessStep>
              ))}
            </div>
          </div>
        </section>

        {/* H2 #4 — Materials & Options */}
        <section className="py-16 md:py-20 bg-muted/40">
          <div className="container px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold mb-10 text-foreground">
              {data.serviceName} Materials and Options in {data.city}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {materialBlocks.map((b, i) => (
                <FeatureBlock key={i} icon={b.icon} title={b.title}>
                  {b.text}
                </FeatureBlock>
              ))}
            </div>
          </div>
        </section>

        {/* H2 #5 — FAQ */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container px-4 max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-10 text-foreground">
              {data.serviceName} FAQs for {data.city}
            </h2>
            <Accordion
              type="single"
              collapsible
              className="w-full bg-card rounded-xl border p-2 shadow-sm"
            >
              {data.faqs.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i + 1}`}
                  className="border-b-0 px-4"
                >
                  <AccordionTrigger className="text-left text-base md:text-lg font-medium hover:no-underline hover:text-accent py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Cross-link nav */}
        <section className="py-12 bg-muted/30 border-t">
          <div className="container px-4 max-w-4xl mx-auto">
            <nav
              aria-label={`${data.city} roofing pages`}
              className="bg-card p-6 rounded-xl border shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
            >
              <p className="font-bold text-base text-foreground">
                More roofing services in {data.city}
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/areas/${data.citySlug}`}
                  className="text-accent hover:text-accent/80 font-medium text-sm inline-flex items-center gap-1"
                >
                  ← All {data.city} services
                </Link>
                <Link
                  to="/contact"
                  className="text-accent hover:text-accent/80 font-medium text-sm inline-flex items-center gap-1"
                >
                  Contact us <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </nav>
          </div>
        </section>

        {/* H2 #6 — CTA */}
        <section className="py-20 md:py-24 bg-primary text-primary-foreground text-center">
          <div className="container px-4 max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Ready to Book Your {data.serviceName} in {data.city}?
            </h2>
            <p className="text-lg text-primary-foreground/85">
              Every quote is fixed-price, in writing, with no surprise fees.
              We'll inspect, diagnose honestly, and recommend only what you
              actually need.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
                asChild
              >
                <Link to="/contact">{ctaLabel}</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 text-white border-white/20 hover:bg-white/20 text-lg px-8 py-6 h-auto"
                asChild
              >
                <a href="tel:6397397969">Call (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

/* Re-export the icons so each template file can import them from here */
export {
  Shield,
  MapPin,
  BadgeCheck,
  Clock,
  FileText,
  Banknote,
  Wrench,
  Hammer,
  CloudRain,
  CheckCircle2,
  ArrowRight,
};
